import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Select, Table, Tag, Progress, Space, Alert, Spin } from "antd";
import { FolderOpenOutlined, FileZipOutlined, WarningOutlined } from "@ant-design/icons";
import { t } from "i18next";
import ApiService from "../../core/service/ApiService";

// Job durumları (backend ile aynı)
const JOB = { WAITING: 0, ANALYZING: 1, ANALYZED: 2, CONFIRMED: 3, SENDING: 4, COMPLETED: 5, ERROR: 6, CANCELLED: 7 };
// EXCLUDED: kullanıcı inceleme ekranında bu çekimin işaretini kaldırdı → PACS'e gönderilmedi.
const STUDY = { PENDING: 0, SENT: 1, ERROR: 2, EXCLUDED: 3 };

// Tablo satır anahtarı. Study UID normalde dolu; boş gelen (UID'siz) tek satır için index'e düşer.
const studyKey = (r, i) => (r?.studyInstanceUid ? r.studyInstanceUid : `#${i}`);

export default function DicomImportModal({ patientId, open, onClose }) {
    const folderInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const pollRef = useRef(null);

    const [pacsList, setPacsList] = useState([]);
    const [firmPacsId, setFirmPacsId] = useState(null);
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [percent, setPercent] = useState(0);
    const [jobId, setJobId] = useState(null);
    const [status, setStatus] = useState(null); // GetStatus sonucu
    // İnceleme ekranında gönderilecek çekimler. null = henüz ilk değer verilmedi.
    // Analiz bittiğinde hepsi işaretli gelir; kullanıcı gereksizlerin işaretini kaldırır.
    const [selectedStudyKeys, setSelectedStudyKeys] = useState(null);

    // Aşama: durumdan türetilir
    const s = status?.status;
    const phase =
        uploading ? 'uploading'
        : !status ? 'select'
        : (s === JOB.WAITING || s === JOB.ANALYZING) ? 'analyzing'
        : s === JOB.ANALYZED ? 'review'
        : (s === JOB.CONFIRMED || s === JOB.SENDING) ? 'sending'
        : s === JOB.COMPLETED ? 'done'
        : s === JOB.ERROR ? 'error'
        : 'select';

    const busy = phase === 'uploading' || phase === 'analyzing' || phase === 'sending';

    const studies = status?.studies || [];
    const selectedKeys = selectedStudyKeys ?? [];
    // Sunucu çekimleri UID ile eşliyor; satır anahtarı UID'siz satırlarda index'e düştüğü için
    // gönderim listesi anahtardan değil kaydın kendisinden türetiliyor.
    const selectedStudyUids = studies
        .filter((r, i) => selectedKeys.includes(studyKey(r, i)))
        .map(r => r.studyInstanceUid ?? '');

    useEffect(() => {
        if (folderInputRef.current) {
            folderInputRef.current.setAttribute('webkitdirectory', '');
            folderInputRef.current.setAttribute('directory', '');
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setFiles([]); setPercent(0); setStatus(null); setFirmPacsId(null); setJobId(null); setUploading(false);
        setSelectedStudyKeys(null);
        loadPacs();
        return () => { stopPoll(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, patientId]);

    // Analiz bitip inceleme ekranı açıldığında varsayılan olarak HEPSİ seçilidir (eski davranış).
    // Polling 2 saniyede bir status'u tazelediği için burada yalnızca ilk değer verilir;
    // kullanıcının kaldırdığı işaretler tazelemede geri gelmez.
    useEffect(() => {
        if (phase !== 'review') return;
        const keys = (status?.studies || []).map(studyKey);
        setSelectedStudyKeys(prev => (prev === null ? keys : prev.filter(k => keys.includes(k))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, status?.studies?.length]);

    const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

    const loadPacs = async () => {
        const res = await ApiService.DicomImport.GetFirmPacs(patientId);
        if (res.success) {
            const list = res.data || [];
            setPacsList(list);
            if (list.length === 1) setFirmPacsId(list[0].id);
        }
    };

    // Aynı dosyayı tekrar eklememek için anahtar (yol/ad + boyut + tarih).
    const fileKey = (f) => `${f.webkitRelativePath || f.name}__${f.size}__${f.lastModified}`;

    // Seçim biriktirilebildiği için (birden fazla klasör + zip) yanlış eklenen bir kaynağın
    // tek başına çıkarılabilmesi gerekir; aksi halde "Temizle" ile hepsini silip baştan
    // seçmek zorunda kalınıyordu. Klasörden gelen dosyalar kök klasör adına göre gruplanır,
    // tek tek eklenen dosyalar kendi başına bir grup olur.
    const fileGroupKey = (f) => (f.webkitRelativePath ? `dir:${f.webkitRelativePath.split('/')[0]}` : `file:${fileKey(f)}`);

    const fileGroups = useMemo(() => {
        const map = new Map();
        files.forEach(f => {
            const key = fileGroupKey(f);
            const existing = map.get(key);
            if (existing) {
                existing.count++;
            }
            else {
                map.set(key, {
                    key,
                    isFolder: !!f.webkitRelativePath,
                    label: f.webkitRelativePath ? f.webkitRelativePath.split('/')[0] : f.name,
                    count: 1
                });
            }
        });
        return Array.from(map.values());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    const removeFileGroup = (key) => setFiles(prev => prev.filter(f => fileGroupKey(f) !== key));

    // Seçimleri BİRİKTİRİR — birden fazla klasör ve zip üst üste eklenebilir.
    const onPick = (e) => {
        const arr = Array.from(e.target.files || []);
        if (arr.length) {
            setFiles(prev => {
                const map = new Map(prev.map(f => [fileKey(f), f]));
                arr.forEach(f => map.set(fileKey(f), f));
                return Array.from(map.values());
            });
        }
        e.target.value = ''; // aynı klasör/dosya tekrar seçilebilsin
    };

    const startUpload = async () => {
        if (!firmPacsId || files.length === 0) return;

        const formData = new FormData();
        formData.append('patientId', patientId);
        formData.append('firmPacsId', firmPacsId);
        files.forEach(f => formData.append('files', f, f.name));

        setUploading(true); setPercent(0);
        const res = await ApiService.DicomImport.Upload(formData, setPercent);
        setUploading(false);
        if (!res.success || !res.data) return;

        setJobId(res.data);
        startPolling(res.data);
    };

    const startPolling = (id) => {
        const tick = async () => {
            const res = await ApiService.DicomImport.GetStatus(id);
            if (res.success && res.data) {
                setStatus(res.data);
                const st = res.data.status;
                if (st === JOB.COMPLETED || st === JOB.ERROR || st === JOB.CANCELLED) stopPoll();
            }
        };
        tick();
        pollRef.current = setInterval(tick, 2000);
    };

    const confirmSend = async () => {
        if (!jobId) return;
        // Yalnızca işaretli çekimler gönderilir; kalanlar sunucuda "çıkarıldı" işaretlenir.
        await ApiService.DicomImport.Confirm(jobId, { studyInstanceUidList: selectedStudyUids });
        // polling zaten SENDING/COMPLETED'ı yakalayacak
    };

    // Kapatma: analiz/inceleme/gönderim öncesi iş varsa sunucuda iptal et (temp temizlensin).
    const handleClose = async () => {
        stopPoll();
        if (jobId && s !== undefined && s !== JOB.COMPLETED && s !== JOB.ERROR && s !== JOB.CANCELLED) {
            try { await ApiService.DicomImport.Cancel(jobId); } catch { /* ignore */ }
        }
        onClose();
    };

    const studyStatusTag = (st) => {
        if (st === STUDY.SENT) return <Tag color="success">{t('Sent')}</Tag>;
        if (st === STUDY.ERROR) return <Tag color="error">{t('Error')}</Tag>;
        if (st === STUDY.EXCLUDED) return <Tag color="default">{t('Excluded')}</Tag>;
        return <Tag color="default">{t('Pending')}</Tag>;
    };

    const columns = [
        {
            title: t('Source Patient'), key: 'source', width: 220,
            render: (_, r) => (
                <span style={{ color: r.mismatch ? '#cf1322' : undefined, fontWeight: r.mismatch ? 600 : undefined }}>
                    {r.mismatch && <WarningOutlined style={{ marginRight: 4 }} />}
                    {r.sourcePatientId || '-'}{r.sourcePatientName ? ` · ${r.sourcePatientName}` : ''}
                </span>
            )
        },
        { title: t('Study Description'), dataIndex: 'studyDescription', key: 'studyDescription', render: (v) => v || '-' },
        { title: t('Accession Number'), dataIndex: 'accessionNumber', key: 'accessionNumber', width: 130, render: (v) => v || '-' },
        { title: t('Series'), dataIndex: 'seriesCount', key: 'seriesCount', width: 70 },
        { title: t('Instances'), dataIndex: 'instanceCount', key: 'instanceCount', width: 80 },
        { title: t('Status'), dataIndex: 'status', key: 'status', width: 100, render: studyStatusTag },
    ];

    let footer;
    if (phase === 'select') {
        footer = [
            <Button key="cancel" onClick={handleClose}>{t('Cancel')}</Button>,
            <Button key="start" type="primary" onClick={startUpload} disabled={!firmPacsId || files.length === 0}>{t('Start Upload')}</Button>,
        ];
    } else if (phase === 'review') {
        footer = [
            <Button key="cancel" danger onClick={handleClose}>{t('Cancel')}</Button>,
            <Button key="confirm" type="primary" onClick={confirmSend} disabled={selectedStudyUids.length === 0}>
                {t('Confirm & Send')}{studies.length > 0 ? ` (${selectedStudyUids.length}/${studies.length})` : ''}
            </Button>,
        ];
    } else if (phase === 'done' || phase === 'error') {
        footer = [<Button key="close" type="primary" onClick={onClose}>{t('Close')}</Button>];
    } else {
        footer = null; // busy: buton yok
    }

    return (
        <Modal
            title={t('Dicom Import')}
            open={open}
            onCancel={() => { if (!busy) handleClose(); }}
            closable={!busy}
            maskClosable={false}
            keyboard={false}
            width={900}
            footer={footer}
        >
            <input ref={folderInputRef} type="file" multiple style={{ display: 'none' }} onChange={onPick} />
            <input ref={fileInputRef} type="file" multiple accept=".zip,.rar,.7z,.dcm,application/dicom,application/zip,*"
                   style={{ display: 'none' }} onChange={onPick} />

            {phase === 'select' && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                        <div style={{ marginBottom: 4 }}>{t('Target PACS')}</div>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('Select PACS')}
                            value={firmPacsId}
                            onChange={setFirmPacsId}
                            options={pacsList.map(p => ({ label: p.name, value: p.id }))}
                        />
                    </div>
                    <Space wrap>
                        <Button icon={<FolderOpenOutlined />} onClick={() => folderInputRef.current?.click()}>{t('Add Folder')}</Button>
                        <Button icon={<FileZipOutlined />} onClick={() => fileInputRef.current?.click()}>{t('Add Zip/Files')}</Button>
                        <span>{files.length > 0 ? `${files.length} ${t('files selected')}` : t('No file selected')}</span>
                        {files.length > 0 && (
                            <Button type="link" size="small" onClick={() => setFiles([])}>{t('Clear')}</Button>
                        )}
                    </Space>

                    {fileGroups.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {fileGroups.map(g => (
                                <Tag
                                    key={g.key}
                                    closable
                                    onClose={() => removeFileGroup(g.key)}
                                    icon={g.isFolder ? <FolderOpenOutlined /> : <FileZipOutlined />}
                                    style={{ marginInlineEnd: 0 }}
                                >
                                    {g.label}{g.isFolder ? ` (${g.count})` : ''}
                                </Tag>
                            ))}
                        </div>
                    )}
                </Space>
            )}

            {phase === 'uploading' && (
                <div>
                    <div style={{ marginBottom: 4 }}>{t('Uploading')}</div>
                    <Progress percent={percent} status="active" />
                </div>
            )}

            {phase === 'analyzing' && (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin /> <div style={{ marginTop: 8 }}>{t('Analyzing DICOM files...')}</div>
                </div>
            )}

            {(phase === 'review' || phase === 'sending' || phase === 'done' || phase === 'error') && status && (
                <div>
                    <div style={{ marginBottom: 8, padding: 8, background: '#fafafa', border: '1px solid #eee', borderRadius: 4 }}>
                        <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>{t('The following patient info will be written to the files')}</div>
                        <Space split="|" wrap>
                            <span><strong>{t('Patient ID')}:</strong> {status.patientCustomId || '-'}</span>
                            <span><strong>{t('Patient')}:</strong> {status.patientName || '-'}</span>
                            <span><strong>{t('Birthdate')}:</strong> {status.patientBirthDate || '-'}</span>
                            <span><strong>{t('Gender')}:</strong> {status.patientGender || '-'}</span>
                        </Space>
                    </div>

                    {phase === 'review' && status.hasMismatch && (
                        <Alert
                            type="warning" showIcon style={{ marginBottom: 8 }}
                            message={t('Some studies do not match the selected patient')}
                            description={(
                                <>
                                    {t('The source Patient ID of the highlighted studies differs from the selected patient. This may be the wrong folder. Review before sending.')}
                                    <br />
                                    {t('Uncheck the studies you do not want to send.')}
                                </>
                            )}
                        />
                    )}
                    {phase === 'review' && !status.hasMismatch && (
                        <Alert type="info" showIcon style={{ marginBottom: 8 }}
                               message={t('Review the studies below, then confirm to send.')}
                               description={t('Uncheck the studies you do not want to send.')} />
                    )}
                    {phase === 'sending' && (
                        <Progress
                            percent={status.totalFiles > 0 ? Math.round((status.processedFiles / status.totalFiles) * 100) : 0}
                            status="active" format={() => `${status.processedFiles}/${status.totalFiles}`}
                            style={{ marginBottom: 8 }}
                        />
                    )}
                    {phase === 'error' && (
                        <Alert type="error" showIcon style={{ marginBottom: 8 }} message={status.message || t('Import failed')} />
                    )}
                    {phase === 'done' && (
                        <Alert type="success" showIcon style={{ marginBottom: 8 }} message={status.message || t('Import completed')} />
                    )}

                    <Table
                        size="small"
                        rowKey={studyKey}
                        columns={columns}
                        dataSource={status.studies || []}
                        pagination={false}
                        // Seçim yalnızca inceleme aşamasında: gönderim başladıktan sonra
                        // liste değiştirilemez.
                        rowSelection={phase === 'review' ? {
                            selectedRowKeys: selectedKeys,
                            onChange: (keys) => setSelectedStudyKeys(keys),
                        } : undefined}
                        onRow={(r) => ({ style: (phase === 'review' && r.mismatch) ? { background: '#fff1f0' } : undefined })}
                        style={{ marginTop: 4 }}
                    />
                </div>
            )}
        </Modal>
    );
}
